/**
 * Data loading for the interaction matrix, kept free of React so it can be
 * tested with a fake client.
 *
 * Safety rules:
 * - Every one of the three queries is checked. A failing query or a thrown
 *   network error results in `loadError`, never in silently missing data.
 * - A technical load failure is never presented as the scientific "unknown"
 *   level, and never as a low risk value.
 */
import { buildInteractions, type MatrixInteraction, type RawInteractionRow } from "@/lib/matrix-risk";

export type QueryResult<T> = { data: T[] | null; error: unknown };

type SelectBuilder<T> = PromiseLike<QueryResult<T>> & {
  eq(column: string, value: unknown): PromiseLike<QueryResult<T>>;
};

/** Minimal structural surface of the Supabase client used by the matrix. */
export type MatrixQueryClient = {
  from(table: string): { select(columns: string): SelectBuilder<never> };
};

type SubstanceRow = {
  id: string;
  name: string;
  sort_order: number | null;
  category_id: number | null;
};

type CategoryRow = { id: number; sort_order: number | null };

export type MatrixLoadResult = {
  substances: string[];
  interactions: MatrixInteraction[];
  /** Raw risk values that are not part of the database enum. */
  invalidRiskValues: string[];
  /** Interaction rows whose substance ids could not be resolved to a name. */
  unresolvedRows: number;
  /** True when a query failed or threw. Distinct from "no rows found". */
  loadError: boolean;
};

const EMPTY: MatrixLoadResult = {
  substances: [],
  interactions: [],
  invalidRiskValues: [],
  unresolvedRows: 0,
  loadError: true,
};

export async function loadMatrixData(client: MatrixQueryClient): Promise<MatrixLoadResult> {
  let subsRes: QueryResult<SubstanceRow>;
  let catsRes: QueryResult<CategoryRow>;
  let intRes: QueryResult<RawInteractionRow>;

  try {
    subsRes = (await client
      .from("substances")
      .select("id, name, sort_order, category_id")) as QueryResult<SubstanceRow>;
    catsRes = (await client
      .from("substance_categories")
      .select("id, sort_order")) as QueryResult<CategoryRow>;
    intRes = (await client
      .from("interaction_details")
      .select("substance_low_id, substance_high_id, risk_level, markers")
      .eq("published", true)) as QueryResult<RawInteractionRow>;
  } catch {
    // Thrown network/runtime failure, for example an aborted request.
    return { ...EMPTY };
  }

  if (subsRes.error || catsRes.error || intRes.error) {
    return { ...EMPTY };
  }

  const rawSubs = subsRes.data ?? [];
  const cats = catsRes.data ?? [];
  const catOrder = new Map<number, number>(cats.map((c) => [c.id, c.sort_order ?? 999]));

  const subs = [...rawSubs].sort((x, y) => {
    const cx = x.category_id != null ? catOrder.get(x.category_id) ?? 999 : 999;
    const cy = y.category_id != null ? catOrder.get(y.category_id) ?? 999 : 999;
    if (cx !== cy) return cx - cy;
    return (x.sort_order ?? 999) - (y.sort_order ?? 999);
  });

  const idToName = new Map(subs.map((s) => [s.id, s.name]));
  const built = buildInteractions(intRes.data ?? [], idToName);

  return {
    substances: subs.map((s) => s.name),
    interactions: built.interactions,
    invalidRiskValues: built.invalidRiskValues,
    unresolvedRows: built.unresolvedRows,
    loadError: false,
  };
}
